import React, {useState, useEffect, useMemo} from 'react';
import { useHistory, useLocation } from 'react-router';
import dayjs from 'dayjs';
// import moment from 'moment'; // Keep if needed for any specific formatting not covered by dayjs
import styles from './JobBoardCandidateTemplatePage.module.css'; // Update CSS import
import {TopBarRedesigned} from "../../../components/TopBarRedesigned";
import {
    Star as StarIcon,
    StarBorder as StarBorderIcon,
    // Event as EventIcon, // Likely not needed for static template
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    // Warning as WarningIcon, // May or may not be needed
    CheckCircle as CheckCircleIcon,
    Assignment as AssignmentIcon, // For Skills/Qualifications
    Info as InfoIcon, // For About Me
    Work as WorkIcon, // For Work Experience
    Email as EmailIcon,
    Phone as PhoneIcon,
    Home as HomeIcon,
    Person as PersonIcon // For name
} from '@mui/icons-material';
import Api from 'api'
import {entities} from 'reduxStore'
import {usePersistedState} from 'hooks'
import { BasicHygienistScoreProps, HYGIENIST_TYPES, HygienistScoreProps, PositionProps, SkillsProps } from "utils/types"; // Keep relevant types
import { formatProfileHygienistName } from "utils"; // Keep relevant utils
import { formatFirstLicensedLabel } from "utils/educationCredentials";
import FixedLoader from "../../../components/FixedLoader";
import AvatarWithInitials from '../../../components/AvatarWithInitials/AvatarWithInitials';
import MaskedContactInfo from '../../../components/JobBoardCandidateTemplate/MaskedContactInfo'; // T2-3743: Import new component
import TokenConfirmationDialog from '../../../components/JobBoardCandidateTemplate/TokenConfirmationDialog'; // T2-3743: Import new dialog
import { PurchaseTokensFlow } from '../../../components/PurchaseTokensFlow'; // Assuming this is the correct path
import _ from "lodash";
import { Button, FormControlLabel, Checkbox, Popover, Typography, Link as MuiLink } from '@mui/material';
import { Application } from 'components/JobBoardDetails';
// Markdown rendering is no longer used for cover letters

// Define the structure for the API responses
interface ProfilePreviewData { // Data structure for the non-sensitive part of the profile
  applicationId: number;
  jobApplicationStatus?: number;
  interviewStatus?: number;
  showContacts?: boolean;
  applicationCreatedOn?: string;
  resumeViewedOn?: string; 
  resumeViewed: boolean; // Crucial to know if it was ever fully accessed

  hygienistId: number;
  firstName?: string; // Masked in preview if token needed
  lastName?: string;  // Masked in preview if token needed
  email?: string | null; // Masked
  phone?: string | null; // Masked
  address?: any | null; // Masked (can be object or string)
  postalCode?: string | null; // Masked, part of address
  CDHONumber?: string | null; // Masked
  
  city?: string; // General location always shown
  province?: string; // General location always shown
  country?: string; // Already converted to string by API
  photoUrl?: string;
  about?: string;
  graduationYear?: number;
  firstLicensedYear?: number | null;
  firstLicensedMonth?: number | null;
  schoolName?: string; 
  hygienistType?: number; 
  isOrtho?: boolean;
  isRestorative?: boolean;
  starScore?: number;
  completedJobs?: number;
  ratingsNum?: number;
  
  skills: SkillsProps[];
  workExperience: PositionProps[];
  coverLetter?: string;

  hygienistStatus?: number; 
  membershipStatusCode?: string | null;
  memberSince?: string; 
  isLeftHanded?: boolean; 
  
  recEngineScore?: number;
  location?: {
    practiceName?: string;
    city?: string;
    province?: string;
  };
}

interface ProfilePreviewResponse {
  profileData: ProfilePreviewData;
  isTokenRequiredForFullView: boolean;
  reasonNoTokenRequired?: string | null;
  tokensRemaining: number;
}

// FullProfileData will be similar to ProfilePreviewData but with all fields guaranteed if accessed
// For simplicity, we can reuse ProfilePreviewData and rely on runtime presence of fields after token spend
type FullProfileData = ProfilePreviewData; 

interface LocationState { 
  candidateData?: FullProfileData; 
  candidateShortCode?: string; // Added to receive the short code
  applicationId?: number; // Add applicationId to location state
  matchScore?: number; // Fresh match score passed from CandidatePage
  starScore?: number; // Fresh star score passed from CandidatePage
  ratingsNum?: number; // Fresh ratings count passed from CandidatePage
}

export const getCandidateMembershipStatusLabel = (statusCode: string): string => {
  switch (statusCode) {
    case 'Elite': return 'Elite';
    case 'Pro': return 'Pro';
    case 'Active': return 'Active';
    case 'Provisional': return 'Provisional';
    case 'CourseRequired': return 'Course Required';
    case 'RL1':
    case 'RL2':
    case 'FL1':
    case 'FL2':
    case 'RL1FL1':
    case 'RL1FL2':
    case 'RL2FL1':
    case 'RL2FL2': return 'Temporary Limited';
    default: return 'Status unavailable';
  }
};

const CollapsibleSection: React.FC<{title: string, icon: React.ReactNode, children: React.ReactNode, defaultOpen?: boolean}> = ({ 
  title, 
  icon, 
  children, 
  defaultOpen = true 
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className={styles.collapsibleSection}>
      <div 
        className={styles.sectionHeader} 
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className={styles.sectionTitle}>
          <span className={styles.sectionIcon}>{icon}</span>
          {title}
        </div>
        <div className={styles.chevronIcon}>
          {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </div>
      </div>
      {isOpen && <div className={styles.sectionContent}>{children}</div>}
    </div>
  );
};

const JobBoardCandidateTemplatePage: React.FC = () => {
  const history = useHistory();
  const location = useLocation<LocationState>();

  const passedCandidateShortCode = location.state?.candidateShortCode;
  const applicationId = location.state?.applicationId;
  const passedMatchScore = location.state?.matchScore; // Get the fresh match score from CandidatePage
  const passedStarScore = location.state?.starScore; // Get the fresh star score from CandidatePage
  const passedRatingsNum = location.state?.ratingsNum; // Get the fresh ratings count from CandidatePage

  // T2-6730: same entity Candidate Overview reads — keep consent in sync on back
  const [persistedApplication, setPersistedApplication] = usePersistedState<Application | null>({
    entityName: entities.dentistCandidateApplication,
    initialValue: null,
  });

  // const [candidateData, setCandidateData] = useState<FullProfileData | null>(null); // Old state
  const [profileData, setProfileData] = useState<ProfilePreviewData | FullProfileData | null>(null); // Can hold preview or full
  const [isTokenRequiredForFullView, setIsTokenRequiredForFullView] = useState<boolean>(true);
  const [tokensRemaining, setTokensRemaining] = useState<number>(0);
  const [contactInfoRevealed, setContactInfoRevealed] = useState<boolean>(false);
  const [showTokenModal, setShowTokenModal] = useState<boolean>(false);
  const [tokensPurchaseOpened, setTokensPurchaseOpened] = useState<boolean>(false); // T2-3743: State for PurchaseTokensFlow
  const [isSavingPreference, setIsSavingPreference] = useState<boolean>(false);
  const [matchScorePopoverAnchorEl, setMatchScorePopoverAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [hiddenNamePopoverAnchorEl, setHiddenNamePopoverAnchorEl] = React.useState<HTMLButtonElement | null>(null); // New state for hidden name popover

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const getMatchScoreColor = (score: number | undefined): string => {
    if (score === undefined) return 'var(--divider, #e0e0e0)';
    const numScore = Number(score);
    if (isNaN(numScore)) return 'var(--divider, #e0e0e0)';
    if (numScore >= 60) return 'var(--success-main, #13CA82)';
    if (numScore >= 40) return 'var(--warning-main, #FFC107)';
    return 'var(--primary-main, #FB7672)';
  };

  const goBack = () => {
    history.goBack();
  }

  useEffect(() => {
    if (!applicationId) {
      setError("No candidate application ID provided.");
      setLoading(false);
      return;
    }

    const fetchInitialData = async (id: number) => {
      setLoading(true);
      setError(null);
      try {
        const previewResponse: ProfilePreviewResponse = await Api.getJobApplicationPreviewProfile({ applicationId: id });
        // If we have a passed match score, use it instead of the API's potentially stale score
        if (passedMatchScore !== undefined && previewResponse.profileData) {
          previewResponse.profileData.recEngineScore = passedMatchScore;
        }
        // If we have passed star score and ratings count, use them too
        if (passedStarScore !== undefined && previewResponse.profileData) {
          previewResponse.profileData.starScore = passedStarScore;
        }
        if (passedRatingsNum !== undefined && previewResponse.profileData) {
          previewResponse.profileData.ratingsNum = passedRatingsNum;
        }
        setProfileData(previewResponse.profileData);
        setIsTokenRequiredForFullView(previewResponse.isTokenRequiredForFullView);
        setTokensRemaining(previewResponse.tokensRemaining);
        
        // If token not required AND resume was already viewed (meaning full access granted previously)
        if (!previewResponse.isTokenRequiredForFullView && previewResponse.profileData.resumeViewed) {
          setContactInfoRevealed(true);
          // Fetch the full data as it might contain more than the initial preview if it was a free pass just now
          const fullData: FullProfileData = await Api.purchaseJobApplicationResume({ applicationId: id });
          // Preserve passed match score in full data too
          if (passedMatchScore !== undefined && fullData) {
            fullData.recEngineScore = passedMatchScore;
          }
          // Preserve passed star score and ratings count too
          if (passedStarScore !== undefined && fullData) {
            fullData.starScore = passedStarScore;
          }
          if (passedRatingsNum !== undefined && fullData) {
            fullData.ratingsNum = passedRatingsNum;
          }
          setProfileData(fullData); 
        } else if (!previewResponse.isTokenRequiredForFullView) {
          // Token not required (e.g. free period, recently viewed but not marked as resumeViewed on preview)
          // but profile not *fully* viewed yet according to resumeViewed flag in preview data.
          // We can directly fetch full data without token spend prompt.
          setContactInfoRevealed(true); // Assume full access is granted
          const fullData: FullProfileData = await Api.purchaseJobApplicationResume({ applicationId: id });
          // Preserve passed match score in full data too
          if (passedMatchScore !== undefined && fullData) {
            fullData.recEngineScore = passedMatchScore;
          }
          // Preserve passed star score and ratings count too
          if (passedStarScore !== undefined && fullData) {
            fullData.starScore = passedStarScore;
          }
          if (passedRatingsNum !== undefined && fullData) {
            fullData.ratingsNum = passedRatingsNum;
          }
          setProfileData(fullData); 
        } else {
          // Token is required, and it hasn't been viewed yet. contactInfoRevealed remains false.
          // UI will show masked data and prompt for token.
          setContactInfoRevealed(false);
        }

      } catch (err: any) {
        console.error("Error fetching candidate profile preview:", err);
        setError(err.message || "Failed to fetch candidate details.");
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData(applicationId);

  }, [applicationId, history, passedMatchScore, passedStarScore, passedRatingsNum]); // history added if used in error navigation

  const handleViewContactInfoClick = () => {
    // Logic to be added: if isTokenRequiredForFullView, setShowTokenModal(true), else call handleConfirmTokenSpendOrGetFullProfile(false)
    if (isTokenRequiredForFullView && tokensRemaining > 0) {
      setShowTokenModal(true);
    } else if (isTokenRequiredForFullView && tokensRemaining <= 0) {
      // T2-3743: When user has 0 tokens, trigger the purchase tokens flow
      setTokensPurchaseOpened(true);
    } else if (!isTokenRequiredForFullView) {
      // If token not required, get full profile directly
      handleConfirmTokenSpendOrGetFullProfile(false); 
    }
  };

  const handleConfirmTokenSpendOrGetFullProfile = async (spendToken: boolean) => {
    // Logic to be added: call Api.purchaseJobApplicationResume, update profileData, setContactInfoRevealed(true), setShowTokenModal(false)
    if (!profileData || !applicationId) return;
    setLoading(true);
    try {
      const fullData: FullProfileData = await Api.purchaseJobApplicationResume({ applicationId: applicationId });
      // If we have a passed match score, preserve it instead of using the API's potentially stale score
      if (passedMatchScore !== undefined && fullData) {
        fullData.recEngineScore = passedMatchScore;
      }
      // If we have passed star score and ratings count, preserve them too
      if (passedStarScore !== undefined && fullData) {
        fullData.starScore = passedStarScore;
      }
      if (passedRatingsNum !== undefined && fullData) {
        fullData.ratingsNum = passedRatingsNum;
      }
      setProfileData(fullData);
      setContactInfoRevealed(true);
      // Potentially update tokensRemaining if the purchaseJobApplicationResume returns it, or re-fetch user tokens.
      // For now, assume the backend handles token deduction and the current tokensRemaining state is sufficient until next page load or explicit refresh.
    } catch (err: any) {
      console.error("Error purchasing resume/fetching full profile:", err);
      setError(err.message || "Failed to process request.");
      // Potentially show a specific error popup for token spend failure
    } finally {
      setLoading(false);
      setShowTokenModal(false);
    }
  };

  const handleSaveShowOfficePreference = async (newPreference: boolean) => {
    if (!profileData || !profileData.applicationId) return;
    setIsSavingPreference(true);
    try {
      await Api.allowShowInfo({ 
        ApplicationId: profileData.applicationId, 
        allow: newPreference 
      });
      setProfileData(prevProfileData => {
        if (!prevProfileData) return null;
        return {
          ...prevProfileData,
          showContacts: newPreference,
        };
      });
      // T2-6730: Overview reads this persisted application for the checkbox
      if (persistedApplication) {
        setPersistedApplication({
          ...persistedApplication,
          showContacts: newPreference,
        });
      }
    } catch (err: any) {
      console.error("Error saving office info preference:", err);
      setError("Failed to save your preference. Please try again.");
    } finally {
      setIsSavingPreference(false);
    }
  };

  const getCountryName = (countryCode: number | undefined | string) => {
    if (typeof countryCode === 'string') return countryCode; // Already processed by backend
    if (countryCode === 0) return "Canada";
    if (countryCode === 1) return "United States";
    return "";
  };

  const formatPhoneNumber = (phone: string | null | undefined): string => {
    if (!phone) return '';
    
    // Remove all non-digit characters
    const digits = phone.replace(/\D/g, '');
    
    // Handle North American phone numbers (10 digits)
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    
    // Handle 11 digit numbers starting with 1 (US/Canada with country code)
    if (digits.length === 11 && digits.startsWith('1')) {
      const remaining = digits.slice(1);
      return `(${remaining.slice(0, 3)}) ${remaining.slice(3, 6)}-${remaining.slice(6)}`;
    }
    
    // For other formats, return the original phone number
    return phone;
  };

  const formatAddress = (profile: FullProfileData) => {
    let addressParts = [];
    if (profile.address) addressParts.push(profile.address);
    if (profile.city) addressParts.push(profile.city);
    if (profile.province) addressParts.push(profile.province);
    if (profile.postalCode) addressParts.push(profile.postalCode);
    // if (profile.country !== undefined) addressParts.push(getCountryName(profile.country)); // Country can be displayed separately or appended
    return addressParts.filter(Boolean).join(', ');
  };
  
  const getHygienistTypeLabel = (typeCode: number | undefined) => {
    switch (typeCode) {
        case HYGIENIST_TYPES.HYGIENIST: return "Dental Hygienist";
        case HYGIENIST_TYPES.ASSISTANT: return "Dental Assistant";
        case HYGIENIST_TYPES.FRONT_DESK: return "Admin/Manager"; // As per hireJob.jobType mapping
        case HYGIENIST_TYPES.ASSOCIATE_DENTIST: return "Associate Dentist";
        default: return "Dental Professional";
    }
  };


  // Memoized professional designation string
  const professionalDesignation = useMemo(() => {
    if (!profileData) return '';
    // Simplified; adapt formatProfileHygienistName or create new util if skills affect designation display here
    return `${profileData.firstName || 'Name Hidden'} ${profileData.lastName || ''} - ${getHygienistTypeLabel(profileData.hygienistType)}`;
  }, [profileData]);

  const renderRatings = () => {
    // Use passed values if available, otherwise fall back to profileData
    const starScore = passedStarScore ?? profileData?.starScore;
    const ratingsNum = passedRatingsNum ?? profileData?.ratingsNum;
    
    if (!profileData || starScore === undefined || ratingsNum === undefined || ratingsNum === 0) {
      // If no ratings, or starScore is undefined, show no rating text
      return <div className={styles.noRatingsTextHeader}>This candidate has not yet received ratings on TempStars</div>;
    }
    const rating = Number(starScore) || 0;
    const ratingCount = Number(ratingsNum) || 0;
    
    return (
      <div className={styles.ratingContainer}>
        <div className={styles.stars}>
          {[1, 2, 3, 4, 5].map((star) => (
            star <= Math.floor(rating) ? 
              <StarIcon key={star} className={styles.star} /> : 
              <StarBorderIcon key={star} className={styles.emptyStar} />
          ))}
        </div>
        <span className={styles.ratingsCount}>
          {rating.toFixed(1)} ({ratingCount} {ratingCount === 1 ? 'Rating' : 'Ratings'})
        </span>
      </div>
    );
  };
  
  const handleMatchScoreInfoClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMatchScorePopoverAnchorEl(event.currentTarget);
  };

  const handleMatchScoreInfoClose = () => {
    setMatchScorePopoverAnchorEl(null);
  };

  const openMatchScoreInfo = Boolean(matchScorePopoverAnchorEl);
  const matchScoreInfoId = openMatchScoreInfo ? 'match-score-popover' : undefined;

  // Handlers for Hidden Name Popover
  const handleHiddenNameInfoClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setHiddenNamePopoverAnchorEl(event.currentTarget);
  };

  const handleHiddenNameInfoClose = () => {
    setHiddenNamePopoverAnchorEl(null);
  };

  const openHiddenNameInfo = Boolean(hiddenNamePopoverAnchorEl);
  const hiddenNameInfoId = openHiddenNameInfo ? 'hidden-name-popover' : undefined;

  if (loading) return <FixedLoader loading={loading} />;
  if (error) {
    return (
      <div className={styles.pageShell}>
        <TopBarRedesigned title="Error" onBack={goBack} />
        <div className={styles.scrollRegion}>
        <div className={styles.errorCard}>
          <p>{error}</p>
        </div>
        </div>
      </div>
    );
  }
  if (!profileData) {
     return (
      <div className={styles.pageShell}>
        <TopBarRedesigned title="Not Found" onBack={goBack} />
        <div className={styles.scrollRegion}>
        <div className={styles.errorCard}>
          <p>Candidate data could not be loaded.</p>
        </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={styles.pageShell}>
        <TopBarRedesigned
          title={'Candidate Profile'}
          onBack={goBack}
        />
        <div className={styles.scrollRegion}>
        {/* Candidate Main Info Card */}
        <div className={styles.hygienistOfferCard}> 
          <div className={styles.profileHeaderMain}>
            <AvatarWithInitials
              photoUrl={contactInfoRevealed ? profileData.photoUrl : null}
              firstName={profileData.firstName}
              lastName={profileData.lastName}
              initialsOverride={contactInfoRevealed ? undefined : (passedCandidateShortCode || undefined)}
              className={styles.profileImage} 
            />
            <div className={styles.profileInfoStack}>
              <h2 className={styles.profileName}>
                {contactInfoRevealed ? 
                  `${profileData.firstName} ${profileData.lastName}${profileData.hygienistType === HYGIENIST_TYPES.HYGIENIST ? ', RDH' : ''}` 
                  : 
                  <>
                    {passedCandidateShortCode || `ID: ${profileData.applicationId}`} {/* Use passed code or fallback */}
                    <MuiLink
                        component="button"
                        variant="caption"
                        onClick={handleHiddenNameInfoClick}
                        aria-describedby={hiddenNameInfoId}
                        className={styles.tooltipLinkBesideName} 
                    >
                        What's this?
                    </MuiLink>
                  </>
                }
              </h2>
              
              {/* Conditional rendering for professional title when masked */}
              {!contactInfoRevealed && profileData.hygienistType !== HYGIENIST_TYPES.HYGIENIST && (
                <p className={styles.professionalTitle}>{getHygienistTypeLabel(profileData.hygienistType)}</p>
              )}
              {/* Professional title when revealed (or for RDH which is part of the name line) */}
              {contactInfoRevealed && profileData.hygienistType !== HYGIENIST_TYPES.HYGIENIST && (
                <p className={styles.professionalTitle}>{getHygienistTypeLabel(profileData.hygienistType)}</p>
              )}
              
              {profileData.graduationYear && (
                  <p className={styles.metaInfoText}>Graduated: {profileData.graduationYear} from {profileData.schoolName}</p> 
              )}
              {formatFirstLicensedLabel(profileData.firstLicensedYear, profileData.firstLicensedMonth) ? (
                  <p className={styles.metaInfoText}>
                    {formatFirstLicensedLabel(profileData.firstLicensedYear, profileData.firstLicensedMonth)}
                  </p>
              ) : null}
            </div>

            {/* Match Score - Styled as a circular gauge in the header */}
            {(passedMatchScore !== undefined || profileData.recEngineScore !== undefined) && (
                <div className={styles.matchScoreHeaderContainer}> 
                    <div className={styles.matchScoreLabelAndTooltipArea}>
                        <div className={styles.matchScoreLabelOutside}>
                            Match Score
                        </div>
                        <MuiLink
                            component="button"
                            variant="caption"
                            onClick={handleMatchScoreInfoClick}
                            aria-describedby={matchScoreInfoId}
                            className={styles.matchScoreTooltipLink}
                        >
                            What's this?
                        </MuiLink>
                    </div>
                    <div 
                        className={styles.matchScoreCircleArea} 
                        title={`Match Score: ${Number(passedMatchScore ?? profileData.recEngineScore)}%`}
                        style={{
                            background: `conic-gradient(${getMatchScoreColor(passedMatchScore ?? profileData.recEngineScore)} ${Number(passedMatchScore ?? profileData.recEngineScore)}%, #e0e0e0 ${Number(passedMatchScore ?? profileData.recEngineScore)}%)`
                        }}
                    >
                        <div className={styles.matchScoreValueInsideCircle} style={{ color: getMatchScoreColor(passedMatchScore ?? profileData.recEngineScore) }}>{Number(passedMatchScore ?? profileData.recEngineScore)}%</div>
                    </div>
                </div>
            )}
          </div>

          {/* Revealed Contact Information - Integrated into top card when revealed */}
          {contactInfoRevealed && (
            <div className={styles.revealedContactInfoSection}>
              <div className={styles.contactItem}>
                  <EmailIcon className={styles.contactIcon} />
                  <span>{profileData.email}</span>
              </div>
              <div className={styles.contactItem}>
                  <PhoneIcon className={styles.contactIcon} />
                  <span>{formatPhoneNumber(profileData.phone)}</span>
              </div>
              <div className={styles.contactItem}>
                  <HomeIcon className={styles.contactIcon} />
                  <span>{formatAddress(profileData as FullProfileData)}</span> 
              </div>
              {profileData.CDHONumber && (
                  <div className={styles.contactItemSimple}> 
                      <span className={styles.label}>License #:</span> {profileData.CDHONumber}
                  </div>
              )}
              {profileData.isLeftHanded !== undefined && (
                <div className={styles.contactItemSimple}> 
                  <span className={styles.label}>Handedness:</span> {profileData.isLeftHanded ? 'Left-handed' : 'Right-handed'}
                </div>
              )}
              {/* Share Your Office Information - Stays here but now part of top card's revealed section */}
              <div className={styles.shareOfficeInfoContainer}>
                  <InfoIcon className={styles.shareOfficeInfoIcon} />
                  <div className={styles.shareOfficeInfoText}>
                      Your office information is currently hidden from this candidate. 
                      Would you like to share your office details with this candidate?
                  </div>
                  <FormControlLabel 
                      control={<Checkbox 
                                  checked={profileData.showContacts || false} 
                                  onChange={(e) => handleSaveShowOfficePreference(e.target.checked)} 
                                  name="shareOfficeDetails" 
                                  disabled={isSavingPreference}
                              />}
                      label={isSavingPreference ? "Saving..." : "Yes, reveal my office details to this candidate"}
                      className={styles.shareOfficeCheckbox}
                  />
              </div>
            </div>
          )}
        </div>

        {/* Contact Info / Token Prompt Area - NEW */}
        {!contactInfoRevealed && isTokenRequiredForFullView && (
          <MaskedContactInfo 
            tokensRemaining={tokensRemaining}
            onViewContactInfoClick={handleViewContactInfoClick}
            onPurchaseTokensClick={() => { setTokensPurchaseOpened(true); }}
          />
        )}
        
        {/* Collapsible Sections for Basic Info, Skills, About Me, Work History need to be created/styled */}
        <CollapsibleSection title="TempStars Work History" icon={<WorkIcon />} defaultOpen={true}>
            {profileData.membershipStatusCode && (
                <div className={styles.basicInfoItem}>
                    <span><span className={styles.label}>TempStars Status:</span> {getCandidateMembershipStatusLabel(profileData.membershipStatusCode)}</span>
                </div>
            )}
            {profileData.memberSince && (
                <div className={styles.basicInfoItem}>
                    <span><span className={styles.label}>Joined TempStars:</span> {dayjs(profileData.memberSince).format('MMMM YYYY')}</span>
                </div>
            )}
            {/* Item 3: Ensure "# of Completed Shifts:" label is correct and value displays */}
            <div className={styles.basicInfoItem}>
                <span>
                    <span className={styles.label}># of Completed Shifts:</span> 
                    {(profileData.completedJobs !== undefined && profileData.completedJobs !== null) ? Number(profileData.completedJobs) : 0}
                </span>
            </div>
            {renderRatings()} 
        </CollapsibleSection>

        {/* Skills & Qualifications Section - Adapted from CustomOfferDetailsPage */}
        {(profileData.skills?.length > 0) && (
          <CollapsibleSection 
            title="Skills & Qualifications (self-reported)" 
            icon={<AssignmentIcon />}
          >
            {(profileData.skills?.length > 0) && (
                 <p className={styles.skillsSubHeader}>Training & Certifications received:</p>
            )}
            {profileData.skills?.map((skill, index) => (
              <div key={index} className={styles.skillItem}>
                <CheckCircleIcon className={styles.checkIcon} />
                <span>{skill.label}</span>
              </div>
            ))}
            {/* Add Ortho/Restorative flags if available and relevant */}
            {profileData.isOrtho && <div className={styles.skillItem}><CheckCircleIcon className={styles.checkIcon} /><span>Orthodontic Certified/Experienced</span></div>}
            {profileData.isRestorative && <div className={styles.skillItem}><CheckCircleIcon className={styles.checkIcon} /><span>Restorative Certified/Experienced</span></div>}

          </CollapsibleSection>
        )}

        {/* Work Experience Section - Adapted */}
        {profileData.workExperience?.length > 0 && (
          <CollapsibleSection 
            title="Work Experience" 
            icon={<WorkIcon />}
          >
            {profileData.workExperience.map((experience, index) => {
              const jobTitle = 'jobTitle' in experience ? experience.jobTitle : '';
              const companyName = 'companyName' in experience ? experience.companyName : '';
              
              return (
                <div key={index} className={styles.workExperienceItem}>
                  <h4 className={styles.jobTitle}>
                    {jobTitle}
                  </h4>
                  <p className={styles.companyName}>
                    {companyName}
                  </p>
                  <p className={styles.jobPeriod}>
                    {experience.fromDate ? dayjs(experience.fromDate).format('MMMM YYYY') : ''} - {
                      experience.current ? 
                        'Present' : 
                        (experience.toDate ? dayjs(experience.toDate).format('MMMM YYYY') : 'N/A')
                    }
                  </p>
                  {experience.description && <p className={styles.jobDescription}>{experience.description}</p>}
                </div>
              );
            })}
          </CollapsibleSection>
        )}

        {/* About Me Section - Adapted */}
        {profileData.about && (
          <CollapsibleSection 
            title="About Me" 
            icon={<InfoIcon />}
          >
            <p className={styles.aboutText}>{profileData.about}</p>
          </CollapsibleSection>
        )}
        
        {profileData.coverLetter && (
          <CollapsibleSection 
            title="Cover Letter" 
            icon={<AssignmentIcon />}
            defaultOpen={true}
          >
            <div className={styles.coverLetterContainer}>
                <div className={styles.coverLetterText}>
                    {profileData.coverLetter}
                </div>
            </div>
          </CollapsibleSection>
        )}
        </div>
      </div>

      {/* Token Confirmation Dialog */}
      <TokenConfirmationDialog 
        open={showTokenModal}
        remainingTokens={tokensRemaining}
        onClose={() => setShowTokenModal(false)}
        onConfirm={() => handleConfirmTokenSpendOrGetFullProfile(true)} // True because this is for explicit spend
        onPurchaseTokens={() => { 
          setShowTokenModal(false); 
          setTokensPurchaseOpened(true); 
        }}
      />

      {tokensPurchaseOpened && (
        <PurchaseTokensFlow 
            onClose={() => setTokensPurchaseOpened(false)} 
            onSuccess={() => { 
                setTokensPurchaseOpened(false);
                // Optionally, re-fetch preview data to update tokensRemaining or attempt to get full profile again
                // For now, user can re-click "View Contact Info"
                if (applicationId) { // Refresh tokens and potentially auto-proceed
                    const id = applicationId;
                    Api.getJobApplicationPreviewProfile({ applicationId: id })
                        .then((previewResponse: ProfilePreviewResponse) => { // Explicitly type previewResponse
                            setTokensRemaining(previewResponse.tokensRemaining);
                            if (previewResponse.tokensRemaining > 0) {
                                setShowTokenModal(true); // Re-open confirmation if they now have tokens
                            }
                        })
                        .catch((err: any) => console.error("Error refreshing preview after token purchase:", err)); // Explicitly type err
                }
            }}
        />
      )}

      <Popover
        id={matchScoreInfoId}
        open={openMatchScoreInfo}
        anchorEl={matchScorePopoverAnchorEl}
        onClose={handleMatchScoreInfoClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
            className: styles.matchScorePopoverPaper
        }}
      >
        <Typography sx={{ p: 2, maxWidth: 300 }}>
          TempStars leverages our data of over 150,000 temping shifts to generate a customized, unique Match Score specifically between your office and this candidate - considering multiple parameters of your office's history and the candidate's track record. A higher score suggests a better potential match.
        </Typography>
      </Popover>

      {/* New Popover for Hidden Name Explanation */}
      <Popover
        id={hiddenNameInfoId}
        open={openHiddenNameInfo}
        anchorEl={hiddenNamePopoverAnchorEl}
        onClose={handleHiddenNameInfoClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
            className: styles.matchScorePopoverPaper // Can reuse or create a new style
        }}
      >
        <Typography sx={{ p: 2, maxWidth: 300 }}>
          We want you to ensure you're spending your tokens wisely, and with full transparency. <br />
          So you get to see all the candidates details before deciding to spend a token to view their contact information to set up an interview. <br />
          When you view their contact information, you're free to contact, interview and hire them if they are a good fit. <br />
          Remember - TempStars doesn't charge for permanent hiring - just a small per-token fee for revealing the contact information of those candidates you think would be a good fit.  
        </Typography>
      </Popover>
    </>
  );
};

export default JobBoardCandidateTemplatePage;
